import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

function formatTimestamp(timestamp) {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleString('nb-NO', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });
}

function formatDistance(meters) {
    if (meters === null || meters === undefined || meters === Infinity) return '-';
    if (meters < 1000) return `${Math.round(meters)}m`;
    return `${(meters / 1000).toFixed(1)}km`;
}

@observer
class GeofenceDebugDrawer extends Component {
    constructor(props) {
        super(props);
        this.state = {
            debugData: null,
            isLoading: true,
            error: null,
            showRawJson: false,
            copied: false,
        };
    }

    componentDidMount() {
        this.loadDebugData();
    }

    loadDebugData = async () => {
        const { raceId, contestantId, contestant, race, raceClass, stores } = this.props;
        const { raceStore } = stores;

        // Check if we have the basic props
        if (!raceId && !race?.id) {
            this.setState({ error: 'Race ID required', isLoading: false });
            return;
        }
        if (!contestantId && !contestant?.id) {
            this.setState({ error: 'Contestant ID required', isLoading: false });
            return;
        }

        const actualRaceId = raceId || race?.id;
        const actualContestantId = contestantId || contestant?.id;

        this.setState({ isLoading: true });

        try {
            const debugData = await raceStore.getGeofenceDebug(actualRaceId, actualContestantId);
            if (debugData) {
                // Merge with passed props for richer data
                if (contestant && !debugData.contestant) {
                    debugData.contestant = {
                        id: contestant.id,
                        bib: contestant.bib,
                        firstname: contestant.firstname,
                        lastname: contestant.lastname,
                        raceClassId: contestant.raceClass,
                        raceClassName: raceClass?.title || raceClass?.name,
                        startTime: contestant.startTime,
                    };
                }
                this.setState({ debugData, isLoading: false });
            } else {
                this.setState({ error: 'Failed to load debug data', isLoading: false });
            }
        } catch (err) {
            this.setState({ error: err.message || 'Failed to load debug data', isLoading: false });
        }
    }

    renderDiagnostics(diagnostics, darkmode) {
        if (!diagnostics) return null;

        const {
            hasGpsDevice,
            deviceIsActive,
            gpsPointsLast24h,
            finishLineCheckpointEnabled,
            finishLineUnlocked,
            closestApproachToFinish,
            pointsInsideFinishArrivalFence,
            suggestedIssue,
        } = diagnostics;

        return (
            <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                <h6 class='mb-3'>
                    <i class='fa-solid fa-stethoscope me-2' />
                    Diagnostic Summary
                </h6>

                {suggestedIssue && (
                    <div class='alert alert-warning mb-3'>
                        <i class='fa-solid fa-triangle-exclamation me-2' />
                        <strong>Suggested Issue:</strong> {suggestedIssue}
                    </div>
                )}

                <div class='row'>
                    <div class='col-6 col-md-4 mb-2'>
                        <div class={`p-2 rounded ${hasGpsDevice ? 'bg-success text-white' : 'bg-danger text-white'}`}>
                            <i class={`fa-solid ${hasGpsDevice ? 'fa-check' : 'fa-times'} me-1`} />
                            GPS Device: {hasGpsDevice ? 'Assigned' : 'NOT Assigned'}
                        </div>
                    </div>
                    <div class='col-6 col-md-4 mb-2'>
                        <div class={`p-2 rounded ${deviceIsActive ? 'bg-success text-white' : 'bg-warning'}`}>
                            <i class={`fa-solid ${deviceIsActive ? 'fa-check' : 'fa-clock'} me-1`} />
                            Device Active: {deviceIsActive ? 'Yes' : 'No'}
                        </div>
                    </div>
                    <div class='col-6 col-md-4 mb-2'>
                        <div class={`p-2 rounded ${gpsPointsLast24h > 0 ? 'bg-success text-white' : 'bg-warning'}`}>
                            <i class='fa-solid fa-location-dot me-1' />
                            GPS Points (24h): {gpsPointsLast24h}
                        </div>
                    </div>
                    <div class='col-6 col-md-4 mb-2'>
                        <div class={`p-2 rounded ${finishLineCheckpointEnabled ? 'bg-success text-white' : 'bg-danger text-white'}`}>
                            <i class={`fa-solid ${finishLineCheckpointEnabled ? 'fa-check' : 'fa-times'} me-1`} />
                            Finish Enabled: {finishLineCheckpointEnabled ? 'Yes' : 'No'}
                        </div>
                    </div>
                    <div class='col-6 col-md-4 mb-2'>
                        <div class={`p-2 rounded ${finishLineUnlocked ? 'bg-success text-white' : 'bg-secondary text-white'}`}>
                            <i class={`fa-solid ${finishLineUnlocked ? 'fa-lock-open' : 'fa-lock'} me-1`} />
                            Finish Unlocked: {finishLineUnlocked ? 'Yes' : 'Locked'}
                        </div>
                    </div>
                    <div class='col-6 col-md-4 mb-2'>
                        <div class={`p-2 rounded ${pointsInsideFinishArrivalFence >= 2 ? 'bg-success text-white' : pointsInsideFinishArrivalFence === 1 ? 'bg-warning' : 'bg-secondary text-white'}`}>
                            <i class='fa-solid fa-bullseye me-1' />
                            Points in Finish Fence: {pointsInsideFinishArrivalFence}
                        </div>
                    </div>
                </div>

                {closestApproachToFinish !== null && (
                    <div class='mt-2 text-muted'>
                        <i class='fa-solid fa-ruler me-1' />
                        Closest approach to finish: <strong>{formatDistance(closestApproachToFinish)}</strong>
                    </div>
                )}
            </div>
        );
    }

    renderGpsDevice(gpsDevice, darkmode) {
        if (!gpsDevice) {
            return (
                <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                    <h6 class='mb-3'>
                        <i class='fa-solid fa-satellite me-2' />
                        GPS Device
                    </h6>
                    <div class='alert alert-danger'>
                        <i class='fa-solid fa-times-circle me-2' />
                        No GPS device assigned to this musher
                    </div>
                </div>
            );
        }

        return (
            <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                <h6 class='mb-3'>
                    <i class='fa-solid fa-satellite me-2' />
                    GPS Device
                </h6>
                <table class={`table table-sm ${darkmode ? 'table-dark' : ''}`}>
                    <tbody>
                        <tr>
                            <td class='text-muted'>Device ID</td>
                            <td><code>{gpsDevice.gpsSerial || gpsDevice.gpsId}</code></td>
                        </tr>
                        <tr>
                            <td class='text-muted'>Name</td>
                            <td>{gpsDevice.name || '-'}</td>
                        </tr>
                        <tr>
                            <td class='text-muted'>Time Window Start</td>
                            <td>{gpsDevice.start ? formatTimestamp(gpsDevice.start) : 'Not set'}</td>
                        </tr>
                        <tr>
                            <td class='text-muted'>Time Window End</td>
                            <td>{gpsDevice.end ? formatTimestamp(gpsDevice.end) : 'Not set'}</td>
                        </tr>
                        <tr>
                            <td class='text-muted'>Currently Active</td>
                            <td>
                                {gpsDevice.isCurrentlyActive ? (
                                    <span class='badge bg-success'>Yes</span>
                                ) : (
                                    <span class='badge bg-warning'>No</span>
                                )}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        );
    }

    renderCheckpoints(checkpoints, darkmode) {
        if (!checkpoints || checkpoints.length === 0) {
            return (
                <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                    <h6 class='mb-3'>
                        <i class='fa-solid fa-flag-checkered me-2' />
                        Checkpoints
                    </h6>
                    <div class='text-muted'>No checkpoints configured</div>
                </div>
            );
        }

        return (
            <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                <h6 class='mb-3'>
                    <i class='fa-solid fa-flag-checkered me-2' />
                    Checkpoints
                </h6>
                <div class='table-responsive'>
                    <table class={`table table-sm ${darkmode ? 'table-dark' : ''}`}>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Type</th>
                                <th>State</th>
                                <th>In/Out Pts</th>
                                <th>Arrival</th>
                                <th>Departure</th>
                                <th>Radius</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {checkpoints.map(cp => {
                                const state = cp.geofenceState;
                                const results = cp.raceResults;
                                const cpType = cp.isStart ? 'START' : cp.isFinish ? 'FINISH' : 'NORMAL';

                                return (
                                    <tr key={cp.md5}>
                                        <td>
                                            <strong>{cp.name}</strong>
                                            <br />
                                            <small class='text-muted'>{cp.md5?.substring(0, 8)}...</small>
                                        </td>
                                        <td>
                                            <span class={`badge ${cp.isStart ? 'bg-primary' : cp.isFinish ? 'bg-success' : 'bg-secondary'}`}>
                                                {cpType}
                                            </span>
                                        </td>
                                        <td>
                                            {state ? (
                                                <>
                                                    <span class={`badge ${
                                                        state.state === 'inside' ? 'bg-success' :
                                                        state.state === 'entering' ? 'bg-warning' :
                                                        state.state === 'exiting' ? 'bg-info' : 'bg-secondary'
                                                    }`}>
                                                        {state.state}
                                                    </span>
                                                </>
                                            ) : (
                                                <span class='text-muted'>-</span>
                                            )}
                                        </td>
                                        <td>
                                            {state ? (
                                                <small>
                                                    {state.pointsInsideFence || 0} / {state.pointsOutsideFence || 0}
                                                </small>
                                            ) : '-'}
                                        </td>
                                        <td>
                                            {results?.arrival ? (
                                                <span class='text-success'>
                                                    <i class='fa-solid fa-check me-1' />
                                                    {results.arrival.isAutoResult && (
                                                        <i class='fa-solid fa-satellite-dish text-info' title='Auto-detected' />
                                                    )}
                                                </span>
                                            ) : (
                                                <span class='text-muted'>-</span>
                                            )}
                                        </td>
                                        <td>
                                            {results?.departure ? (
                                                <span class='text-success'>
                                                    <i class='fa-solid fa-check me-1' />
                                                    {results.departure.isAutoResult && (
                                                        <i class='fa-solid fa-satellite-dish text-info' title='Auto-detected' />
                                                    )}
                                                </span>
                                            ) : (
                                                <span class='text-muted'>{cp.isFinish ? 'N/A' : '-'}</span>
                                            )}
                                        </td>
                                        <td>
                                            <small>
                                                {cp.geofenceArrivalRadius}m / {cp.geofenceDepartureRadius}m
                                            </small>
                                        </td>
                                        <td>
                                            {!cp.geofenceEnabled ? (
                                                <span class='badge bg-danger'>Disabled</span>
                                            ) : cp.isUnlocked ? (
                                                <span class='badge bg-success'>
                                                    <i class='fa-solid fa-lock-open me-1' />
                                                    Unlocked
                                                </span>
                                            ) : (
                                                <span class='badge bg-warning' title={cp.lockReason}>
                                                    <i class='fa-solid fa-lock me-1' />
                                                    Locked
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    renderGpsPoints(gpsPoints, checkpoints, darkmode) {
        if (!gpsPoints || gpsPoints.length === 0) {
            return (
                <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                    <h6 class='mb-3'>
                        <i class='fa-solid fa-location-dot me-2' />
                        Recent GPS Points
                    </h6>
                    <div class='text-muted'>No GPS points available</div>
                </div>
            );
        }

        // Find finish checkpoint for highlighting
        const finishCp = checkpoints?.find(cp => cp.isFinish);

        return (
            <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                <div class='d-flex justify-content-between align-items-center mb-3'>
                    <h6 class='mb-0'>
                        <i class='fa-solid fa-location-dot me-2' />
                        Recent GPS Points (last {gpsPoints.length})
                    </h6>
                    <button
                        type='button'
                        class='btn btn-sm btn-outline-primary'
                        onClick={this.loadDebugData}
                    >
                        <i class='fa-solid fa-refresh me-1' />
                        Refresh
                    </button>
                </div>
                <div class='table-responsive' style='max-height: 400px; overflow-y: auto;'>
                    <table class={`table table-sm ${darkmode ? 'table-dark' : ''}`}>
                        <thead style='position: sticky; top: 0;'>
                            <tr>
                                <th>Timestamp</th>
                                <th>Coordinates</th>
                                <th>Speed</th>
                                {checkpoints && checkpoints.map(cp => (
                                    <th key={cp.md5} title={cp.name}>
                                        <small>{cp.name?.substring(0, 8)}</small>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {gpsPoints.map((point, idx) => (
                                <tr key={point.id || idx}>
                                    <td>
                                        <small>{formatTimestamp(point.timestamp)}</small>
                                    </td>
                                    <td>
                                        <small class='font-monospace'>
                                            {point.lat?.toFixed(4)}, {point.lng?.toFixed(4)}
                                        </small>
                                    </td>
                                    <td>
                                        <small>{point.speed ? `${point.speed.toFixed(1)} km/h` : '-'}</small>
                                    </td>
                                    {point.distances && point.distances.map(dist => {
                                        const isFinish = finishCp && dist.checkpointMd5 === finishCp.md5;
                                        return (
                                            <td key={dist.checkpointMd5}>
                                                <small class={`${
                                                    dist.isInsideArrivalFence ? 'text-success fw-bold' :
                                                    dist.isInsideDepartureFence ? 'text-warning' : ''
                                                } ${isFinish ? 'bg-light rounded px-1' : ''}`}>
                                                    {formatDistance(dist.distanceM)}
                                                    {dist.isInsideArrivalFence && (
                                                        <i class='fa-solid fa-check ms-1' />
                                                    )}
                                                </small>
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div class='mt-2 text-muted small'>
                    <i class='fa-solid fa-check text-success me-1' /> = Inside arrival fence
                </div>
            </div>
        );
    }

    renderConstants(constants, darkmode) {
        if (!constants) return null;

        return (
            <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                <h6 class='mb-3'>
                    <i class='fa-solid fa-cog me-2' />
                    Geofence Constants
                </h6>
                <div class='row'>
                    <div class='col-6 col-md-3'>
                        <div class='text-muted small'>Required Points</div>
                        <div><strong>{constants.REQUIRED_POINTS}</strong></div>
                    </div>
                    <div class='col-6 col-md-3'>
                        <div class='text-muted small'>Fast Passthrough</div>
                        <div><strong>{Math.round(constants.FAST_PASSTHROUGH_TIMEOUT_MS / 60000)} min</strong></div>
                    </div>
                    <div class='col-6 col-md-3'>
                        <div class='text-muted small'>Same Location Lockout</div>
                        <div><strong>{Math.round(constants.SAME_LOCATION_LOCKOUT_MS / 3600000)} hours</strong></div>
                    </div>
                    <div class='col-6 col-md-3'>
                        <div class='text-muted small'>Min Time Between Events</div>
                        <div><strong>{Math.round(constants.MIN_TIME_BETWEEN_EVENTS_MS / 60000)} min</strong></div>
                    </div>
                </div>
            </div>
        );
    }

    toggleRawJson = () => {
        this.setState({ showRawJson: !this.state.showRawJson, copied: false });
    }

    copyToClipboard = () => {
        const { debugData } = this.state;
        const jsonText = JSON.stringify(debugData, null, 2);
        navigator.clipboard.writeText(jsonText).then(() => {
            this.setState({ copied: true });
            setTimeout(() => this.setState({ copied: false }), 2000);
        });
    }

    renderRawJson(debugData, darkmode) {
        const { showRawJson, copied } = this.state;

        const prompt = `Here is the geofence debug data for musher #${debugData.contestant?.bib} ${debugData.contestant?.firstname} ${debugData.contestant?.lastname}. Please help me understand why the finish line checkpoint is not triggering:

\`\`\`json
${JSON.stringify(debugData, null, 2)}
\`\`\``;

        return (
            <div class={`rounded-lg p-3 mb-3 ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                <div class='d-flex justify-content-between align-items-center mb-2'>
                    <h6 class='mb-0' style='cursor: pointer;' onClick={this.toggleRawJson}>
                        <i class={`fa-solid ${showRawJson ? 'fa-chevron-down' : 'fa-chevron-right'} me-2`} />
                        Raw JSON for Claude
                    </h6>
                    {showRawJson && (
                        <button
                            type='button'
                            class={`btn btn-sm ${copied ? 'btn-success' : 'btn-outline-primary'}`}
                            onClick={this.copyToClipboard}
                        >
                            <i class={`fa-solid ${copied ? 'fa-check' : 'fa-copy'} me-1`} />
                            {copied ? 'Copied!' : 'Copy to clipboard'}
                        </button>
                    )}
                </div>
                {showRawJson && (
                    <>
                        <div class='text-muted small mb-2'>
                            Copy this and paste it into Claude to get help debugging:
                        </div>
                        <textarea
                            class='form-control font-monospace'
                            style='font-size: 0.7em; height: 400px; white-space: pre;'
                            readOnly
                            value={prompt}
                        />
                    </>
                )}
            </div>
        );
    }

    render() {
        const { debugData, isLoading, error } = this.state;
        const { userStore } = this.props.stores;
        const { user = {} } = userStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');

        if (isLoading && !debugData) {
            return (
                <div class='w-100 d-flex justify-content-center align-items-center' style='min-height: 200px;'>
                    <div class='spinner-border text-primary' role='status'>
                        <span class='visually-hidden'>Loading...</span>
                    </div>
                </div>
            );
        }

        if (error) {
            return (
                <div class='w-100 p-4'>
                    <div class='alert alert-danger'>{error}</div>
                </div>
            );
        }

        if (!debugData) {
            return (
                <div class='w-100 p-4'>
                    <div class='alert alert-warning'>No debug data available</div>
                </div>
            );
        }

        const { contestant, gpsDevice, checkpoints, gpsPoints, constants, diagnostics } = debugData;

        return (
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100 h-100' style='max-width: 1200px; padding: 20px; padding-bottom: 100px;'>

                    <div class='d-flex justify-content-between align-items-center mb-4'>
                        <h5 class='mb-0'>
                            <i class='fa-solid fa-bug me-2' />
                            Geofence Debug: #{contestant?.bib} {contestant?.firstname} {contestant?.lastname}
                            {isLoading && <span class='spinner-border spinner-border-sm ms-2' />}
                        </h5>
                        <div>
                            <span class='badge bg-secondary me-2'>{contestant?.raceClassName}</span>
                        </div>
                    </div>

                    {this.renderDiagnostics(diagnostics, darkmode)}
                    {this.renderGpsDevice(gpsDevice, darkmode)}
                    {this.renderCheckpoints(checkpoints, darkmode)}
                    {this.renderGpsPoints(gpsPoints, checkpoints, darkmode)}
                    {this.renderConstants(constants, darkmode)}
                    {this.renderRawJson(debugData, darkmode)}

                </div>
            </div>
        );
    }
}

export default GeofenceDebugDrawer;
